/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { MutableSelectionModel } from '@gh/common-shared';
import { GridRowEvent } from '@gh/core-ui-grid';
import { coalesce } from '@gh/core-util';
import { isNil } from 'lodash';
import { PbComponentItemFinderDialogConfig } from '../../resources/pb-component-item-finder-business.service';
import {
  getPbComponentItemFinderListItemId,
  PbComponentItemFinderListItem,
} from '../../resources/pb-component-item-finder.model';

export abstract class AbstractPbComponentItemFinderViewComponent {
  protected abstract input: PbComponentItemFinderDialogConfig;
  protected abstract selectionModel: MutableSelectionModel<PbComponentItemFinderListItem>;

  get hasSelected(): boolean {
    return !!this.selectionModel && this.selectionModel.hasChanges();
  }

  get multiselection(): boolean {
    return coalesce(this.input.multiselection, false);
  }

  get isNumericSelector(): boolean {
    return coalesce(this.input.isNumericSelector, false);
  }

  onRowClicked(event: GridRowEvent<PbComponentItemFinderListItem>): void {
    if (this.multiselection) {
      this.toggle(event.data);
    } else {
      this.singleSelect(event.data);
    }
  }

  onToggle(row: PbComponentItemFinderListItem): void {
    this.toggle(row);
  }

  onSingleSelect(row: PbComponentItemFinderListItem): void {
    this.singleSelect(row);
  }

  isDisabled(row?: PbComponentItemFinderListItem): boolean {
    return !this.input.unselectable && row && this.isPreselected(row) || false;
  }

  isSelected(row?: PbComponentItemFinderListItem): boolean {
    return isNil(row)
      || this.input.multiselection && this.selectionModel.has(getPbComponentItemFinderListItemId(row))
      || this.isSelectedNow(row);
  }

  changeCount(row: PbComponentItemFinderListItem, count: number): void{
    if(!row) return;    
    this.selectionModel.updateCount(getPbComponentItemFinderListItemId(row), count)
  }

  getCount(item: PbComponentItemFinderListItem): number {
    if(!item) return 0;
    const items = this.selectionModel.getNewEntities();
    const matchedItem = items.find((i: PbComponentItemFinderListItem) => i.uniqueId === item.uniqueId);
    if (!matchedItem) return 0;
    return  matchedItem.count ?? 1;
    }

  increaseCount(item: PbComponentItemFinderListItem): void {
    const newCount = this.getCount(item) + 1;
    if(newCount === 1) this.onToggle(item)
    this.changeCount(item, newCount);
  }

  decreaseCount(item: PbComponentItemFinderListItem): void {
    const currentCount = this.getCount(item);
    if (currentCount === 0) return;
    const newCount = currentCount - 1;
    if (newCount === 0) this.onToggle(item);
    this.changeCount(item, newCount);
  }

  private isSelectedNow(row: PbComponentItemFinderListItem): boolean {
    return this.selectionModel.hasNew(getPbComponentItemFinderListItemId(row));
  }

  private singleSelect(row: PbComponentItemFinderListItem): void {
    if (this.isPreselected(row)) {
      return;
    }

    const selectedId = this.selectionModel.getNewIds()[0];
    if (selectedId) {
      this.selectionModel.remove(selectedId);
    }
    this.selectionModel.add(row);
  }

  private toggle(row: PbComponentItemFinderListItem): void {
    if (this.isSelected(row)) {
      if (this.input.unselectable) {
        this.unselect(row);
      } else if (!this.isPreselected(row)) {
        this.unselect(row);
      }
    } else {
      this.select(row);
    }
  }

  private select(row: PbComponentItemFinderListItem): void {
    this.selectionModel.add(row);
  }

  private unselect(row: PbComponentItemFinderListItem): void {
    this.selectionModel.remove(getPbComponentItemFinderListItemId(row));
  }

  private isPreselected(row: PbComponentItemFinderListItem): boolean {
    return this.selectionModel.hasOld(getPbComponentItemFinderListItemId(row));
  }
}
